/**
 * AccountHistoryModal — Shows last 20 vouchers for a given GL account.
 * Used by ERPFinanceGrid via the "Acct History" context menu action.
 */
import React, { useState, useEffect } from 'react';
import { X, RefreshCw } from 'lucide-react';
import {API_BASE, getToken} from '../../lib/api';
const API_URL = API_BASE;


const AccountHistoryModal = ({ accountId, accountName, onClose }) => {
  const [loading, setLoading] = useState(true);
  const [data, setData]       = useState([]);
  const [error, setError]     = useState('');

  // Date range — defaults to current month
  const today = new Date().toISOString().split('T')[0];
  const firstOfMonth = (() => { const d = new Date(); return `${d.getFullYear()}-${String(d.getMonth()+1).padStart(2,'0')}-01`; })();
  const [fromDate, setFromDate] = useState(firstOfMonth);
  const [toDate, setToDate]     = useState(today);

  const load = async () => {
    setLoading(true);
    setError('');
    try {
      const tok = getToken();
      const params = new URLSearchParams({ accountId, limit: 200, fromDate, toDate });
      const res = await fetch(
        `${API_URL}/api/finance/account-history?${params}`,
        { headers: { Authorization: `Bearer ${tok}` } }
      );
      const json = await res.json();
      if (json.success) setData(json.data || []);
      else setError(json.error || 'Failed to load account history');
    } catch (e) {
      setError(e.message);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, [accountId, fromDate, toDate]);

  const fa = (v) => (parseFloat(v) || 0).toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 });

  return (
    <div
      style={{
        position: 'fixed', inset: 0, zIndex: 1100,
        background: 'rgba(0,0,0,0.45)',
        display: 'flex', alignItems: 'center', justifyContent: 'center',
      }}
      onClick={(e) => e.target === e.currentTarget && onClose()}
      data-testid="account-history-overlay"
    >
      <div style={{
        background: 'var(--erp-card-bg, #fff)',
        border: '1px solid var(--erp-border)',
        borderRadius: 10,
        width: 760,
        maxWidth: '95vw',
        maxHeight: '80vh',
        display: 'flex',
        flexDirection: 'column',
        boxShadow: '0 20px 60px rgba(0,0,0,0.25)',
      }} data-testid="account-history-modal">

        {/* Header */}
        <div style={{
          display: 'flex', alignItems: 'center', justifyContent: 'space-between',
          padding: '12px 16px',
          borderBottom: '1px solid var(--erp-border)',
          background: 'var(--erp-grid-header-bg)',
          borderRadius: '10px 10px 0 0',
        }}>
          <div>
            <div style={{ fontSize: 13, fontWeight: 700, color: '#fff' }}>Account History</div>
            <div style={{ fontSize: 11, color: 'rgba(255,255,255,0.8)', marginTop: 1 }}>
              {accountName || accountId} — Last 20 transactions
            </div>
          </div>
          <div style={{ display: 'flex', gap: 6 }}>
            <button
              onClick={load}
              style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#fff', padding: 4 }}
              title="Refresh"
              data-testid="account-history-refresh-btn"
            >
              <RefreshCw size={14} />
            </button>
            <button
              onClick={onClose}
              style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#fff', padding: 4 }}
              data-testid="account-history-close-btn"
            >
              <X size={16} />
            </button>
          </div>
        </div>

        {/* Date Range Filter */}
        <div style={{
          display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap',
          padding: '8px 16px',
          borderBottom: '1px solid var(--erp-border)',
          background: 'var(--erp-bg, #fafafa)',
          fontSize: 12,
        }}>
          <span style={{ color: 'var(--erp-text-muted)', fontWeight: 600, fontSize: 11 }}>Period:</span>
          <input
            type="date" value={fromDate}
            onChange={e => setFromDate(e.target.value)}
            data-testid="history-from-date"
            style={{ padding: '3px 8px', border: '1px solid var(--erp-border)', borderRadius: 4, fontSize: 11, background: 'var(--erp-card-bg, #fff)' }}
          />
          <span style={{ color: 'var(--erp-text-muted)', fontSize: 11 }}>→</span>
          <input
            type="date" value={toDate}
            onChange={e => setToDate(e.target.value)}
            data-testid="history-to-date"
            style={{ padding: '3px 8px', border: '1px solid var(--erp-border)', borderRadius: 4, fontSize: 11, background: 'var(--erp-card-bg, #fff)' }}
          />
          <span style={{ color: 'var(--erp-text-muted)', fontSize: 10, marginLeft: 4 }}>({data.length} record{data.length !== 1 ? 's' : ''})</span>
        </div>

        {/* Body */}
        <div style={{ flex: 1, overflow: 'auto', padding: 0 }}>
          {loading ? (
            <div style={{ padding: 40, textAlign: 'center', color: 'var(--erp-text-muted)', fontSize: 13 }}>
              Loading history...
            </div>
          ) : error ? (
            <div style={{ padding: 24, textAlign: 'center', color: 'var(--erp-danger)', fontSize: 12 }}>
              {error}
            </div>
          ) : data.length === 0 ? (
            <div style={{ padding: 40, textAlign: 'center', color: 'var(--erp-text-muted)', fontSize: 13 }}>
              No transactions found for this account.
            </div>
          ) : (
            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 12 }}>
              <thead>
                <tr>
                  {['#', 'Date', 'Voucher No', 'Type', 'Narration', 'Dr Amount', 'Cr Amount'].map(h => (
                    <th key={h} style={{
                      padding: '7px 10px',
                      background: '#f0f4ff',
                      color: '#3a4a6b',
                      fontWeight: 700,
                      fontSize: 11,
                      borderBottom: '2px solid #d0daf0',
                      textAlign: h.includes('Amount') ? 'right' : 'left',
                      position: 'sticky', top: 0, zIndex: 1,
                      whiteSpace: 'nowrap',
                    }}>{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {data.map((row, i) => (
                  <tr key={i}
                    data-testid={`history-row-${i}`}
                    style={{ background: i % 2 === 0 ? '#fff' : '#f4f7ff' }}
                  >
                    <td style={{ padding: '5px 10px', color: 'var(--erp-text-muted)', textAlign: 'center' }}>{i + 1}</td>
                    <td style={{ padding: '5px 10px', fontFamily: 'monospace', fontSize: 11 }}>{row.DocDate || '—'}</td>
                    <td style={{ padding: '5px 10px', fontWeight: 600, color: '#1a56db', fontFamily: 'monospace', fontSize: 11 }}>{row.DocId}</td>
                    <td style={{ padding: '5px 10px' }}>
                      <span style={{
                        background: '#e8f4ff', color: '#1a56db',
                        padding: '1px 6px', borderRadius: 10, fontSize: 10, fontWeight: 600
                      }}>{row.VoucherType}</span>
                    </td>
                    <td style={{ padding: '5px 10px', color: 'var(--erp-text-muted)', maxWidth: 200, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                      {row.Narration || '—'}
                    </td>
                    <td style={{ padding: '5px 10px', textAlign: 'right', fontFamily: 'monospace', color: parseFloat(row.DrAmount) > 0 ? '#16a34a' : 'var(--erp-text-muted)' }}>
                      {parseFloat(row.DrAmount) > 0 ? fa(row.DrAmount) : '-'}
                    </td>
                    <td style={{ padding: '5px 10px', textAlign: 'right', fontFamily: 'monospace', color: parseFloat(row.CrAmount) > 0 ? '#dc2626' : 'var(--erp-text-muted)' }}>
                      {parseFloat(row.CrAmount) > 0 ? fa(row.CrAmount) : '-'}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>

        {/* Footer */}
        <div style={{
          padding: '8px 16px',
          borderTop: '1px solid var(--erp-border)',
          display: 'flex', justifyContent: 'space-between', alignItems: 'center',
          fontSize: 11, color: 'var(--erp-text-muted)',
        }}>
          <span>A/c ID: <strong>{accountId}</strong></span>
          <span>{fromDate} — {toDate}</span>
        </div>
      </div>
    </div>
  );
};

export default AccountHistoryModal;
